import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import cx from "classnames";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import {
  Grid,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Tooltip,
} from "@material-ui/core";

// @material-ui/icons
import EditIcon from "@material-ui/icons/Edit";
import Button from "components/CustomButtons/Button.js";
// Custom styles
import styles from "assets/jss/material-dashboard-pro-react/components/headingStyle.js";
import { Add, ImportExport, Publish } from "@material-ui/icons";
import WaveTitle from "views/Dashboard/SolutionGenerator/WaveTitle";
import MarkdownEditorWithPreview from "components/MarkdownEditor/MarkdownEditorWithPreview";

const useStyles = makeStyles(styles);

export default function HeadingWithDescription(props) {
  // Destructure props
  const {
    textAlign,
    title,
    shortDescription,
    longDescription = "",
    identifier,
    onChange,
    showImportIcon = false,
    showEditIcon = true,
    showCreateIcon = false,
    showPublishIcon = false,
    setImportModal = null,
    setAssetModal = null,
    setPublishAlert = null,
  } = props;

  // Use custom styles
  const classes = useStyles();

  // State to control modal visibility and for editable fields
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editableTitle, setEditableTitle] = useState(title);
  const [editableShortDescription, setEditableShortDescription] = useState(
    shortDescription
  );
  const [editableLongDescription, setEditableLongDescription] = useState(
    longDescription
  );

  useEffect(() => {
    setEditableTitle(title || "");
    setEditableShortDescription(shortDescription || "");
    setEditableLongDescription(longDescription || "");
  }, [title, shortDescription, longDescription]);

  // Combine the classes with dynamic text alignment
  const heading =
    classes.heading +
    " " +
    cx({
      [classes[textAlign + "TextAlign"]]: textAlign !== undefined,
    });

  // Handle changes for all fields (contenteditable)
  const handleChange = (field) => (event) => {
    // Call the passed `onChange` method with the field name and the new value
    onChange(field, event.target.innerText);
  };

  // Handle modal open
  const handleEditModalOpen = () => {
    setOpenEditModal(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setOpenEditModal(false);
  };

  // Handle save changes
  const handleSaveChanges = () => {
    // Create a JSON object with all the fields
    const updatedData = {
      title: editableTitle,
      shortDescription: editableShortDescription,
      longDescription: editableLongDescription,
    };

    // Pass the JSON object to the parent via the onChange prop
    onChange(updatedData);

    // Close the edit modal
    setOpenEditModal(false);
  };

  function capitalizeFirstLetter(word) {
    if (!word) return ""; // Handle empty input
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }

  return (
    <div className={heading}>
      {/* Title and Icons in the same container */}
      <Grid container justifyContent="space-between" alignItems="flex-start">
        {/* Title on the left */}
        <Grid item xs={12} sm={10}>
          {title !== undefined ? (
            <Typography variant="h4">
              <WaveTitle text={title} />
            </Typography>
          ) : null}
        </Grid>

        {/* Icons on the right */}
        <Grid item xs={12} sm={2} style={{ textAlign: "right" }}>
          {showPublishIcon && (
            <Tooltip title={`Publish Project`} placement="top">
              <IconButton onClick={() => setPublishAlert(true)}>
                <Publish />
              </IconButton>
            </Tooltip>
          )}
          {showImportIcon && (
            <Tooltip title={`Import`} placement="top">
              <IconButton onClick={() => setImportModal(true)}>
                <ImportExport />
              </IconButton>
            </Tooltip>
          )}
          {showCreateIcon && (
            <Tooltip title={`Create`} placement="top">
              <IconButton onClick={() => setAssetModal(true)}>
                <Add />
              </IconButton>
            </Tooltip>
          )}
          {showEditIcon && (
            <Tooltip
              title={`Edit ${capitalizeFirstLetter(identifier)} Details`}
              placement="top"
            >
              <IconButton onClick={handleEditModalOpen}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
        </Grid>
        <Grid item xs={12} >
          <div>
            {shortDescription !== undefined ? (
              <Typography
                variant="body1"
                style={{
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {shortDescription}
              </Typography>
            ) : null}
          </div>
        </Grid>
      </Grid>

      {/* Edit Modal to edit title, short description, and course information */}
      <Dialog
        open={openEditModal}
        onClose={handleModalClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Edit {capitalizeFirstLetter(identifier)} Details
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Title"
            value={editableTitle}
            onChange={(e) => setEditableTitle(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Short Description"
            value={editableShortDescription}
            onChange={(e) => setEditableShortDescription(e.target.value)}
            fullWidth
            margin="normal"
          />

          {editableLongDescription !== "" && (
            <>
              <Typography
                variant="caption"
                style={{
                  marginTop: "15px",
                  marginBottom: "15px",
                  color: "#6b6b6b",
                }}
              >
                {capitalizeFirstLetter(identifier)} Information
              </Typography>
              <div data-color-mode="light">
                <MarkdownEditorWithPreview
                  value={editableLongDescription}
                  onChange={setEditableLongDescription}
                />
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose} color="quCreateColor">
            Cancel
          </Button>
          <Button onClick={handleSaveChanges} color="quCreateColor">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

HeadingWithDescription.propTypes = {
  title: PropTypes.node,
  shortDescription: PropTypes.node,
  longDescription: PropTypes.node,
  textAlign: PropTypes.oneOf(["right", "left", "center"]),
  identifier: PropTypes.string,
  onChange: PropTypes.func.isRequired, // `onChange` is a required prop
};
